import { Modal } from 'antd';
import React, { FC } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { getUserName, useRole } from 'entities/User';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { Payment } from 'entities/Payment';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';
import { InvoiceItemType } from 'entities/Invoice';
import { Deposit } from 'entities/Deposit';

interface CreditAndRefundDetailsModalProps {
  payment: Payment;
  deposit?: Deposit;
  isOpen: boolean;
  onClose: () => void;
}

const DescriptionText: FC<{ description: string }> = ({ description }) => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const { description: text, documentNumber } = splitDocumentDescription(description);

  return (
    <p className="px-5 py-1 italic">
      {t(text, { ns: 'common' })} {documentNumber}
    </p>
  );
};

export const CreditAndRefundDetailsModal: FC<CreditAndRefundDetailsModalProps> = ({ payment, deposit, isOpen, onClose }) => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const { isManagement } = useRole();
  const currencySymbol = useGetCurrencySymbol();

  return (
    <Modal title={`${t('Payment')} - ${payment.documentNumber} Details`} open={isOpen} onCancel={onClose} width={1050} footer={null}>
      <div className="grid grid-cols-2 gap-6 my-8">
        <div className="border-r border-primary pr-6">
          {payment.creditNote?.creditNoteItems && (
            <div>
              <div>{t('Credit Note Information')}</div>
              <BulletsTable
                theme="clear"
                rows={[
                  {
                    label: t('Credit Note'),
                    value: payment.creditNote.documentNumber,
                  },
                  {
                    label: t('Date'),
                    value: getClientDateFormat(payment.creditNote.createdAt),
                  },
                  {
                    label: t('Type'),
                    value: t(payment.creditNote.creditNoteType, { ns: 'common' }),
                    emptyValueSymbol: '-',
                  },
                  ...(isManagement && payment.creditNote.issuedBy
                    ? [
                        {
                          label: t('Cancelled by'),
                          value: getUserName(payment.creditNote.issuedBy) || '-',
                        },
                      ]
                    : []),
                ]}
              />
              {payment.creditNote.description && <DescriptionText description={payment.creditNote.description} />}
              {payment.creditNote.creditNoteItems.map((item, index) => {
                const isRentalItem = item.creditNoteItemType === InvoiceItemType.RENTAL_ITEM;

                const creditNoteItemType = isRentalItem
                  ? item.invoiceItem?.contractItem?.rentalItem?.rentalItemType || item.creditNoteItemType
                  : item.creditNoteItemType;

                const itemHeading = t(creditNoteItemType);

                const rows: TableRow[] = [
                  {
                    label: t('Credited amount'),
                    value: `${roundNumber(item.creditedAmount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                  {
                    label: t('VAT'),
                    value: `${roundNumber(item.vatRateAmount)} ${currencySymbol} (${item.vatRatePercent}%)`,
                    emptyValueSymbol: '0%',
                  },
                ];

                const depositChargedAmount = deposit?.chargedDepositAmount || 0;

                if (item.creditNoteItemType === InvoiceItemType.DEPOSIT && depositChargedAmount > 0) {
                  rows.push({
                    label: t('Charged amount'),
                    value: `${roundNumber(depositChargedAmount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  });
                }

                if (item.extraAmount > 0) {
                  rows.push({
                    label: t('Extra amount'),
                    value: `${roundNumber(item.extraAmount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  });
                }

                if (item.dateFrom && item.dateTo) {
                  rows.push({
                    label: t('Period'),
                    value: `${getClientDateFormat(item.dateFrom)} - ${getClientDateFormat(item.dateTo)}`,
                    emptyValueSymbol: '-',
                  });
                }

                if (item.description) {
                  const splitedDescription = splitDocumentDescription(item.description);
                  rows.push({
                    label: t('Description'),
                    value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
                    emptyValueSymbol: '-',
                  });
                }

                return (
                  <div key={item.creditNoteItemId}>
                    <BulletsTable heading={itemHeading} theme="clear" rows={rows} />
                  </div>
                );
              })}
              <BulletsTable
                theme="clear"
                rows={[
                  {
                    label: t('Total credited'),
                    value: `${roundNumber(payment.creditNote.creditedAmount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                    highlighted: true,
                  },
                ]}
              />
            </div>
          )}
        </div>

        <div className="pl-6">
          <div>{t('Refund Information')}</div>
          <BulletsTable
            theme="clear"
            rows={
              payment.refund
                ? [
                    {
                      label: t('Refund'),
                      value: payment.refund.documentNumber,
                    },
                    {
                      label: t('Date'),
                      value: getClientDateFormat(payment.refund.createdAt),
                    },
                    ...(isManagement && payment.refundedBy
                      ? [
                          {
                            label: t('Refunded by'),
                            value: getUserName(payment.refundedBy) || '-',
                          },
                        ]
                      : []),
                    {
                      label: t('Refund method'),
                      value: t(payment.refund.paymentMethod || '-', { ns: 'common' }),
                    },
                    {
                      label: t('Transaction ID'),
                      value: payment.refund.transactionId,
                      emptyValueSymbol: '-',
                    },
                  ]
                : []
            }
          />
          {payment.refund ? (
            <>
              {payment.refund.refundItems?.map((item) => {
                const rows: TableRow[] = [
                  {
                    label: t('Refund amount'),
                    value: `${roundNumber(item.amount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                  {
                    label: t('VAT'),
                    value: `${roundNumber(item.vatRateAmount)} ${currencySymbol} (${item.vatRatePercent}%)`,
                    emptyValueSymbol: '0%',
                  },
                ];

                if (item.description) {
                  const splitedDescription = splitDocumentDescription(item.description);
                  rows.push({
                    label: t('Description'),
                    value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
                    emptyValueSymbol: '-',
                  });
                }

                return (
                  <div key={item.refundItemId}>
                    <BulletsTable heading={item.refundItemType ? t(item.refundItemType) : undefined} theme="clear" rows={rows} />
                  </div>
                );
              })}
              <BulletsTable
                theme="clear"
                rows={[
                  ...(isManagement && payment.refund.description
                    ? [
                        {
                          label: t('Description'),
                          value: (() => {
                            const splitedDescription = splitDocumentDescription(payment.refund.description);
                            return `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`;
                          })(),
                          emptyValueSymbol: '-',
                        },
                      ]
                    : []),
                  {
                    label: t('Total refunded'),
                    value: `${roundNumber(payment.refund.amount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                    highlighted: true,
                  },
                ]}
              />
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-center text-primaryLight">
              <div>
                <div className="text-lg mb-2">{t('No refund yet')}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
